import React, { useState, useEffect } from 'react';
import { FaPrint, FaEye, FaDownload, FaShare } from 'react-icons/fa';
import { FiFileText, FiTrendingUp, FiDollarSign, FiClock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { sendApiRequest } from '../utils.js';
import { motion, AnimatePresence } from 'framer-motion';

const ListInvoices = () => {
  const userData = useSelector(state => state.user_info[0]?.user);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const response = await sendApiRequest(`api/packages/all_manual/receipts?partnerEmail=${userData?.email}`, {}, "GET");
        setReceipts(response.data.data.receipts);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Error fetching receipts');
        setLoading(false);
      }
    };

    if (userData?.email) {
      fetchReceipts();
    }
  }, [userData?.email]);

  // Separate receipts into commission and business
  const commissionReceipts = receipts.filter(receipt => receipt.isCommissionReceipt === true);
  const businessReceipts = receipts.filter(receipt => receipt.isCommissionReceipt === false);

  // Filter based on active tab and search
  const getFilteredReceipts = () => {
    let filtered = [];
    if (activeTab === 'all') {
      filtered = receipts;
    } else if (activeTab === 'commission') {
      filtered = commissionReceipts;
    } else {
      filtered = businessReceipts;
    }
    
    if (searchTerm) {
      filtered = filtered.filter(receipt => 
        receipt.receiptNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        receipt.package?.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const filteredReceipts = getFilteredReceipts();

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getTotalAmount = (receiptsList) => {
    return receiptsList.reduce((sum, receipt) => sum + (receipt.amount || 0), 0);
  };

  const openReceiptModal = (receipt) => {
    setSelectedReceipt(receipt);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedReceipt(null);
  };

  const handlePrint = (receiptId) => {
    window.open(`/dashboard/packages/view-receipt?receiptId=${receiptId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FiFileText className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading your receipts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl p-8 shadow-xl max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <FiAlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Receipts</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  My Receipts
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">View all your transaction receipts and invoices</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">
                  Total: {formatAmount(getTotalAmount(receipts))}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Receipts</p>
                <p className="text-2xl font-bold text-gray-800">{receipts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <FiFileText className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Commission Receipts</p>
                <p className="text-2xl font-bold text-purple-600">{commissionReceipts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <FiTrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Business Receipts</p>
                <p className="text-2xl font-bold text-blue-600">{businessReceipts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <FiDollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs and Search */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-3">
              <div className="flex gap-2">
                {[
                  { id: 'all', label: 'All Receipts', count: receipts.length, color: 'indigo' },
                  { id: 'commission', label: 'Commission', count: commissionReceipts.length, color: 'purple' },
                  { id: 'business', label: 'Business', count: businessReceipts.length, color: 'blue' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-xl font-medium transition-all ${
                      activeTab === tab.id
                        ? `bg-${tab.color}-600 text-white shadow-md`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>
              
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by receipt number or package..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none w-full sm:w-64"
                />
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Receipts Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Receipt No.</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">Package</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">Type</th>
                  <th className="px-4 sm:px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                <AnimatePresence>
                  {filteredReceipts.length > 0 ? (
                    filteredReceipts.map((receipt, index) => (
                      <motion.tr
                        key={receipt._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.03 }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                        <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                          <span className="font-mono text-sm font-semibold text-indigo-600">
                            {receipt.receiptNumber}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <FiClock className="w-3 h-3 text-gray-400" />
                            {new Date(receipt.receiptDate).toLocaleDateString('en-IN')}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 whitespace-nowrap text-sm text-gray-700 hidden sm:table-cell">
                          {receipt.package?.title || 'N/A'}
                        </td>
                        <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                          <span className="font-bold text-gray-800">{formatAmount(receipt.amount)}</span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 whitespace-nowrap hidden md:table-cell">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                            receipt.isCommissionReceipt 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {receipt.isCommissionReceipt ? (
                              <FiTrendingUp className="w-3 h-3" />
                            ) : (
                              <FiDollarSign className="w-3 h-3" />
                            )}
                            {receipt.isCommissionReceipt ? 'Commission' : 'Business'}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handlePrint(receipt._id)}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition-all group"
                              title="Print Receipt"
                            >
                              <FaPrint size={14} className="group-hover:scale-110 transition" />
                            </button>
                            <button
                              onClick={() => openReceiptModal(receipt)}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition-all group"
                              title="View Details"
                            >
                              <FaEye size={14} className="group-hover:scale-110 transition" />
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-16 h-16 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FiFileText className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-gray-500 font-medium">No receipts found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filter</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Summary Footer */}
          {filteredReceipts.length > 0 && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  Showing {filteredReceipts.length} of {receipts.length} receipts
                </p>
                <p className="text-sm font-semibold text-gray-700">
                  Total Amount: <span className="text-indigo-600">{formatAmount(getTotalAmount(filteredReceipts))}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Details Modal */}
      <AnimatePresence>
        {showModal && selectedReceipt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={closeModal}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 sticky top-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <FiFileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-white font-semibold text-lg">Receipt Details</h2>
                      <p className="text-indigo-100 text-sm">{selectedReceipt.receiptNumber}</p>
                    </div>
                  </div>
                  <button onClick={closeModal} className="text-white hover:bg-white/20 rounded-lg p-1 transition">
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Receipt Number</p>
                    <p className="font-mono font-semibold text-indigo-600">{selectedReceipt.receiptNumber}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Receipt Date</p>
                    <p className="font-semibold text-gray-800">
                      {new Date(selectedReceipt.receiptDate).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Package Title</p>
                    <p className="font-semibold text-gray-800">{selectedReceipt.package?.title || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Amount</p>
                    <p className="text-2xl font-bold text-indigo-600">{formatAmount(selectedReceipt.amount)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Receipt Type</p>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                      selectedReceipt.isCommissionReceipt 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {selectedReceipt.isCommissionReceipt ? 'Commission Receipt' : 'Business Receipt'}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Status</p>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-700">
                      <FiCheckCircle className="w-3 h-3" />
                      Completed
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handlePrint(selectedReceipt._id)}
                    className="flex-1 bg-indigo-600 text-white py-2 rounded-xl font-semibold hover:bg-indigo-700 transition flex items-center justify-center gap-2"
                  >
                    <FaPrint className="w-4 h-4" />
                    Print Receipt
                  </button>
                  <button
                    onClick={closeModal}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-xl font-semibold hover:bg-gray-200 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ListInvoices;